/**
 * The project timeline endpoints.
 *
 * Its own file rather than another section of `lib/projects.ts`, which is past
 * seven hundred lines and already describes the register, the summary, the
 * milestones, the time entries, the expenses and the contracts (AGENTS.md §4).
 * The timeline shares nothing with any of those but a project id.
 *
 * The row type is `Activity` from `lib/clients.ts` - the SAME type the client
 * and juhiklient timelines use, imported rather than redeclared. That is the
 * whole point of the feature on the server side too: a note on a project is an
 * `Activity` of type "note", not a record of its own, so a second interface here
 * would be a copy that drifts the first time a column is added and would quietly
 * stop `ActivityList` from accepting these rows.
 */
import { api } from "./api.js";
import type { Activity } from "./clients.js";

/** One page of the timeline, newest first. The server's paging envelope. */
export interface ProjectActivityPage {
  rows: Activity[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/**
 * A page of the project's timeline.
 *
 * The default page size is the server's (50) rather than a number repeated here:
 * a client-side default is a second opinion about how much a page is, and the
 * one that decides is the one that runs the query.
 */
export const getProjectActivity = (projectId: string, page?: number, pageSize?: number) => {
  const q = new URLSearchParams();
  if (page !== undefined) q.set("page", String(page));
  if (pageSize !== undefined) q.set("pageSize", String(pageSize));
  const query = q.toString();
  return api.get<ProjectActivityPage>(
    `/admin/crm/projects/${projectId}/activity${query ? `?${query}` : ""}`,
  );
};

/**
 * Leave a note on the project.
 *
 * There is no `type` in the body on purpose. The endpoint fixes it to "note" -
 * the only kind a person can mean - so this signature cannot be talked into
 * filing something that looks like an automatic entry.
 */
export const addProjectNote = (projectId: string, body: string) =>
  api.post<Activity>(`/admin/crm/projects/${projectId}/activity`, { body });
